<template>
  <div>
    <Header />

    <div class="Auth">
      <div class="Auth__image"></div>
      <div class="Auth__form">
        <!-- Step 1: email -->
        <div v-if="step === 'email'" class="w-full">
          <h1 class="md:text-4xl mb-3 text-3xl font-extrabold leading-tight">Welcome Back!</h1>
          <p class="mb-6 text-lg">Please enter your e-mail to sign in.</p>

          <TextInput
            v-model="email"
            class="w-full"
            placeholder="email@example.com"
            type="email"
            autocomplete="email"
            @keyup.enter="!isSubmitting && submitEmail()"
          />

          <div v-if="error" class="FormGroup__label my-2">
            <InputErrorMessage :error="error" />
          </div>

          <AppButton primary class="w-full mt-4" :disabled="isSubmitting" @click="submitEmail">Continue</AppButton>

          <p class="pt-6 mt-8 border-t">
            Don't have an account?
            <router-link :to="{ name: 'join' }" class="text-link font-bold">Sign up &raquo;</router-link>
          </p>
        </div>

        <!-- Step 2: code -->
        <div v-else class="w-full">
          <h1 class="md:text-4xl mb-3 text-3xl font-extrabold leading-tight">Check your email</h1>
          <p class="mb-6 text-lg">
            Enter the 6-digit code we sent to
            <strong>{{ email }}</strong>
          </p>

          <TextInput
            ref="codeInput"
            v-model="code"
            class="CodeInput w-full"
            placeholder="------"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            pattern="[0-9]*"
            :error="codeError"
            @keyup.enter="verifyCode"
          />

          <AppButton primary class="w-full mt-4" :disabled="isVerifying || code.length !== 6" @click="verifyCode">
            {{ isVerifying ? 'Signing in…' : 'Sign in' }}
          </AppButton>

          <p class="mt-4 text-sm text-gray-600">Or use the sign-in link in the same email.</p>

          <div class="flex items-center justify-between pt-6 mt-8 text-sm border-t">
            <button type="button" class="text-link font-bold" @click="changeEmail">&larr; Use a different email</button>
            <button
              type="button"
              class="text-link font-bold"
              :class="{ 'opacity-50 cursor-not-allowed': resendIn > 0 }"
              :disabled="resendIn > 0"
              @click="resendCode"
            >
              {{ resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import EMAIL_REGEXP from 'constants/emailRegexp'

import ProfileService from 'services/ProfileService'
import Header from 'components/Header'
import Footer from 'components/Footer'

const RESEND_COOLDOWN = 30

export default {
  components: {
    Header,
    Footer
  },

  data() {
    return {
      step: 'email',
      error: undefined,
      email: undefined,
      isSubmitting: false,
      code: '',
      codeError: undefined,
      isVerifying: false,
      resendIn: 0,
      resendTimer: null
    }
  },

  computed: {
    ...mapGetters('profile', ['user'])
  },

  watch: {
    code(val) {
      // Keep the field numeric and capped at 6 digits, then auto-submit once full.
      const digits = (val || '').replace(/\D/g, '').slice(0, 6)
      if (digits !== val) {
        this.code = digits
        return
      }
      // Clear the error only while the user is typing a new code — not when we
      // programmatically reset the field to '' after a failed attempt, which
      // would otherwise wipe the error message before it's seen.
      if (digits.length > 0) {
        this.codeError = undefined
      }
      if (digits.length === 6 && !this.isVerifying) {
        this.verifyCode()
      }
    }
  },

  mounted() {
    if (this.user) {
      this.$router.replace({ name: 'dashboard' })
    }
  },

  beforeDestroy() {
    clearInterval(this.resendTimer)
  },

  methods: {
    async submitEmail() {
      this.error = undefined
      this.isSubmitting = true
      if (this.email && EMAIL_REGEXP.test(this.email)) {
        try {
          await ProfileService.createSignInLink(this.email)
          this.step = 'code'
          this.code = ''
          this.startResendCooldown()
          this.$nextTick(this.focusCode)
        } catch (e) {
          const { message } = (e.response && e.response.data) || {}

          this.error = message || 'Unknown error'
        }
      } else {
        this.error = 'Please enter valid email'
      }

      this.isSubmitting = false
    },

    async verifyCode() {
      if (this.isVerifying) return
      if (!/^\d{6}$/.test(this.code)) {
        this.codeError = 'Please enter the 6-digit code'
        return
      }

      this.codeError = undefined
      this.isVerifying = true
      try {
        await ProfileService.validateSignInCode(this.email, this.code)

        // Full page load so the app boots with the freshly established session.
        const { href } = this.$router.resolve({ name: 'dashboard' })
        window.location = window.location.origin + href
      } catch (e) {
        const { message } = (e.response && e.response.data) || {}

        this.codeError = message || 'Unknown error'
        this.code = ''
        this.isVerifying = false
        this.$nextTick(this.focusCode)
      }
    },

    async resendCode() {
      if (this.resendIn > 0) return

      this.codeError = undefined
      try {
        await ProfileService.createSignInLink(this.email)
        this.code = ''
        this.startResendCooldown()
        this.$nextTick(this.focusCode)
      } catch (e) {
        const { message } = (e.response && e.response.data) || {}

        this.codeError = message || 'Unknown error'
      }
    },

    changeEmail() {
      this.step = 'email'
      this.code = ''
      this.codeError = undefined
      clearInterval(this.resendTimer)
      this.resendIn = 0
    },

    startResendCooldown() {
      clearInterval(this.resendTimer)
      this.resendIn = RESEND_COOLDOWN
      this.resendTimer = setInterval(() => {
        this.resendIn -= 1
        if (this.resendIn <= 0) clearInterval(this.resendTimer)
      }, 1000)
    },

    focusCode() {
      const input = this.$refs.codeInput && this.$refs.codeInput.$el.querySelector('input')
      if (input) input.focus()
    }
  },

  metaInfo() {
    return {
      title: 'Sign-In',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: 'Sign in to FormBeat to access your Dashboard'
        }
      ]
    }
  }
}
</script>

<style lang="postcss" scoped>
.CodeInput ::v-deep .input {
  @apply font-bold text-center;
  font-size: 28px;
  letter-spacing: 12px;
  /* Offset the trailing letter-spacing so the digits read centered. */
  padding-left: 12px;
}
</style>
